import { StatCard } from '@/components/common/StatCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminScope } from '@/contexts/AdminScopeContext';
import { getDocumentStatistics } from '@/services/documentApi';
import { getUserStatistics } from '@/services/userApi';
import { Clock, FileText, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UserStats {
  total: number;
  byRole: {
    role_user: number;
    role_admin: number;
    role_superadmin: number;
  };
  byStatus: {
    active: number;
    pending: number;
    inactive: number;
  };
}

interface DocStats {
  total: number;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { selectedYear, isLoading: scopeLoading } = useAdminScope();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [docStats, setDocStats] = useState<DocStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (scopeLoading) return;

    async function loadDashboardData() {
      setIsLoading(true);
      try {
        const [users, docs] = await Promise.all([
          getUserStatistics(),
          getDocumentStatistics(undefined, selectedYear),
        ]);
        setUserStats(users);
        setDocStats(docs);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadDashboardData();
  }, [selectedYear, scopeLoading]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Admin Dashboard
        </h1>
        <p className="text-muted-foreground">
          Overview of users and documents in the system
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={userStats?.total || 0}
          description={`${userStats?.byStatus.active || 0} active`}
          icon={Users}
        />
        <StatCard
          title="Total Documents"
          value={docStats?.total || 0}
          description="All uploaded documents"
          icon={FileText}
        />
        <StatCard
          title="Pending Users"
          value={userStats?.byStatus.pending || 0}
          description="Awaiting verification"
          icon={Clock}
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common administrative tasks
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={() => navigate('/admin/users')}>
            <Users className="mr-2 h-4 w-4" />
            Manage Users
          </Button>
          {/* <Button variant="outline" onClick={() => navigate('/admin/documents')}>
            <FileText className="mr-2 h-4 w-4" />
            View All Documents
          </Button> */}
        </CardContent>
      </Card>

      {/* Detailed Stats */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* User Stats Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">By Status</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg bg-green-500/10">
                    <p className="text-2xl font-bold text-green-600">{userStats?.byStatus.active || 0}</p>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-yellow-500/10">
                    <p className="text-2xl font-bold text-yellow-600">{userStats?.byStatus.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-gray-500/10">
                    <p className="text-2xl font-bold text-gray-600">{userStats?.byStatus.inactive || 0}</p>
                    <p className="text-xs text-muted-foreground">Inactive</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">By Role</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{userStats?.byRole.role_user || 0}</p>
                    <p className="text-xs text-muted-foreground">Users</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{userStats?.byRole.role_admin || 0}</p>
                    <p className="text-xs text-muted-foreground">Admins</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted">
                    <p className="text-2xl font-bold">{userStats?.byRole.role_superadmin || 0}</p>
                    <p className="text-xs text-muted-foreground">Super Admins</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Stats Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center p-4">
              <p className="text-3xl font-bold">{docStats?.total || 0}</p>
              <p className="text-sm text-muted-foreground">Total documents</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
